import { RightEnum } from "@finway-group/shared/lib/models"
import { notification } from "antd"
import React, { Suspense, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Redirect, Route, Switch } from "react-router-dom"
import { FullScreenLoader } from "~Components/FullScreenLoader"
import { Loading } from "~Components/loading"
import ScrollToTop from "~Components/scrollToTop"
import { ConfirmEmail, ForgotPassword, Login, NewPasswordPage, ResetPasswordPage } from "~Features/authentication"
import { AuthErrorPage } from "~Features/authentication/AuthErrorPage"
import ErrorPage from "~Features/errors/ErrorPage"
import { NotFoundPage } from "~Features/errors/NotFoundPage"
import { LiquidityContainer } from "~Features/pages/liquidity/liquidity.container"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { SelectedInboxInvoicesProvider } from "~Shared/context/selectedExpenses.context"
import { useApiVersion } from "~Shared/hooks/apiVersion.hooks"
import { useAuthWithNavigation } from "~Shared/hooks/auth.hooks"
import { useCompanyById } from "~Shared/hooks/company.hooks"
import { useIsFreeVersion, useIsPurchaseOrderEnabled } from "~Shared/hooks/featureFlags.hooks"
import { useLoggedInEmployeeProfile } from "~Shared/queries/employee.queries"
import { ErrorRoute } from "~Shared/router/ErrorRoute"
import { FullScreenRoute, PrivateFullScreen } from "~Shared/router/FullScreenRoute"
import { LegacyRouteRedirect, legacyRoutes } from "~Shared/router/LegacyRouteRedirect"
import { PrivateRoute } from "~Shared/router/PrivateRoute"
import { CompanyScopedRoutePathEnum, RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { RouterPathnameChainRecorder } from "~Shared/router/RouterPathnameChainRecorder"
import { AuthzService } from "~Shared/services"
import { lazily } from "~Shared/utils/react.utils"
import { generatePath } from "~Shared/utils/routing.utils"

// lazy loading
const { SettingsPage } = lazily(() => import("~Features/settings/SettingsPage"))
const { Intercom } = lazily(() => import("~Components/intercom"))

const { Dashboard } = lazily(() => import("~Features/pages/dashboard/dashboard"))
const { ExpenseDetailsPage } = lazily(() => import("~Features/pages/expenses/expenseDetails/ExpenseDetailsPage"))

const { CostCentersList } = lazily(() => import("~Features/pages/costCenters/CostCentersList"))
const { CostCenterDetail } = lazily(() => import("~Features/pages/costCenters/costCenterDetails/costCenterDetails.container"))

const { EmployeesPage } = lazily(() => import("~Features/pages/employees/EmployeesPage"))
const { EmployeeDetail } = lazily(() => import("~Features/pages/employees/employeeDetails.container"))

const { VendorsList } = lazily(() => import("~Features/pages/vendors/VendorsList"))
const { VendorDetail } = lazily(() => import("~Features/pages/vendors/vendor/vendorDetails.container"))

const { SubscriptionsList } = lazily(() => import("~Features/pages/subscriptions/SubscriptionsList"))
const { SubscriptionDetails } = lazily(() => import("~Features/pages/subscriptions/subscriptionDetails.container"))

const { CreditCardsContainer } = lazily(() => import("~Features/pages/creditCards/CreditCardsContainer"))
const { CreditCardDetail } = lazily(() => import("~Features/pages/creditCards/creditCardContainer/creditCardDetails.container"))
const { WalletDetailsContainer } = lazily(() => import("~Features/pages/creditCards/walletDetails/walletDetails.container"))

const { TransactionsList } = lazily(() => import("~Features/pages/transactions/TransactionsList"))
const { TransactionsDetails } = lazily(() => import("~Features/pages/transactions/transactionsDetails.container"))

const { WorkflowsList } = lazily(() => import("~Features/pages/workflows/WorkflowsList"))

const { Reports } = lazily(() => import("~Features/pages/reporting/Reports.container"))

const { PurchaseOrdersListPage } = lazily(() => import("~Features/pages/purchaseOrders/PurchaseOrdersListPage"))
const { PurchaseOrderDetailsPage } = lazily(() => import("~Features/pages/purchaseOrders/PurchaseOrdersDetailsPage"))

const { DeliveryNotesListPage } = lazily(() => import("~Features/pages/deliveryNotes/DeliveryNotesListPage"))

const { DeliveryNoteDetailsPage } = lazily(() => import("~Features/pages/deliveryNotes/DeliveryNoteDetailsPage"))

const { OposListPage } = lazily(() => import("~Features/pages/opos/OposListPage"))

export const AppRouter = () => {
    const { t } = useTranslation()
    const { isAuthenticated, logout, companyId, featureFlags } = useAuthWithNavigation()
    const isPurchaseOrderEnabled = useIsPurchaseOrderEnabled()

    const activeCompany = useCompanyById(companyId ?? "")
    const cardsEnabled = activeCompany?.cardsEnabled

    const isFreeVersion = useIsFreeVersion()
    const loggedInEmployee = useLoggedInEmployeeProfile()

    const { checkedVersion } = useApiVersion()

    useEffect(() => {
        if (isAuthenticated && companyId) {
            // Make sure to update the token by logging loggedInEmployee out if any feature flag is missing
            if (featureFlags && (featureFlags.paidVersion === undefined || featureFlags.stripeEnabled === undefined || featureFlags.outgoingWireTransferEnabled === undefined)) {
                notification.open({
                    type: "info",
                    message: t("notification:new_api_version.title"),
                    description: t("notification:new_api_version.message"),
                    duration: 0,
                })
                logout()
            }

            loggedInEmployee?.setActiveCompanyProfile(companyId)
        }
    }, [loggedInEmployee, companyId])

    return !checkedVersion ? (
        <FullScreenLoader />
    ) : (
        <Suspense fallback={<Loading />}>
            {PROCESS_ENV.IS_INTERCOM_ENABLED && (
                <Suspense fallback={<Loading />}>
                    <Intercom />
                </Suspense>
            )}
            <ScrollToTop />
            <RouterPathnameChainRecorder />

            <Switch>
                <FullScreenRoute exact path={RoutePathEnum.LOGIN}>
                    <Login />
                </FullScreenRoute>
                <FullScreenRoute path={RoutePathEnum.FORGOT_PASSWORD}>
                    <ForgotPassword />
                </FullScreenRoute>
                <PrivateFullScreen path={RoutePathEnum.NEW_PASSWORD}>
                    <NewPasswordPage />
                </PrivateFullScreen>
                <FullScreenRoute path={RoutePathEnum.RESET_TOKEN}>
                    <ResetPasswordPage />
                </FullScreenRoute>
                <FullScreenRoute path={RoutePathEnum.CONFIRM_EMAIL}>
                    <ConfirmEmail />
                </FullScreenRoute>

                {/* // TODO: @John and @Vittorio re-add */}
                {/* <FullScreenRoute path="/deforce-sso/:code">
                        <DeforceSso />
                    </FullScreenRoute> */}

                {/* Company-scoped settings routes (new format) */}
                <PrivateRoute path={CompanyScopedRoutePathEnum.SETTINGS} isCompanyScoped includedFreeVersion>
                    <Suspense fallback={<Loading />}>
                        <SettingsPage />
                    </Suspense>
                </PrivateRoute>

                {[CompanyScopedRoutePathEnum.ROOT, RoutePathEnum.ROOT].map((path) => (
                    <PrivateRoute exact path={path} includedFreeVersion key={path} isCompanyScoped>
                        <Redirect to={{ pathname: isFreeVersion ? "/liquidity" : generatePath(CompanyScopedRoutePathEnum.EXPENSES, {}) }} />
                    </PrivateRoute>
                ))}

                {/* Company-scoped expense routes (new format) */}
                <PrivateRoute exact path={CompanyScopedRoutePathEnum.EXPENSES} isCompanyScoped>
                    <Suspense fallback={<Loading />}>
                        <Dashboard />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.OPOS_LIST} isCompanyScoped canSee={AuthzService.canLoggedInUserSeeAllExpenses()}>
                    <Suspense fallback={<Loading />}>
                        <OposListPage />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.EXPENSE_DETAIL} isCompanyScoped>
                    <SelectedInboxInvoicesProvider>
                        <Suspense fallback={<Loading />}>
                            <ExpenseDetailsPage />
                        </Suspense>
                    </SelectedInboxInvoicesProvider>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.FOLDER_EXPENSE_DETAIL} isCompanyScoped>
                    <SelectedInboxInvoicesProvider>
                        <Suspense fallback={<Loading />}>
                            <ExpenseDetailsPage />
                        </Suspense>
                    </SelectedInboxInvoicesProvider>
                </PrivateRoute>

                {/* Company-scoped subscriptions routes */}
                <PrivateRoute exact path={CompanyScopedRoutePathEnum.SUBSCRIPTIONS} isCompanyScoped>
                    <Suspense fallback={<Loading />}>
                        <SubscriptionsList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.SUBSCRIPTION_DETAIL} isCompanyScoped>
                    <Suspense fallback={<Loading />}>
                        <SubscriptionDetails />
                    </Suspense>
                </PrivateRoute>

                {/* MIND THE ORDER! This needs to be BEFORE CreditCardDetail */}
                <PrivateRoute
                    exact
                    path={CompanyScopedRoutePathEnum.WALLET_DETAIL}
                    canSee={cardsEnabled && AuthzService.isRightGrantedForLoggedInUser(RightEnum.CARD_WALLET__ALL__READ)}
                    isCompanyScoped
                >
                    <Suspense fallback={<Loading />}>
                        <WalletDetailsContainer />
                    </Suspense>
                </PrivateRoute>

                {/* Company-scoped cards routes */}
                <PrivateRoute exact path={CompanyScopedRoutePathEnum.CARDS} isCompanyScoped canSee={cardsEnabled}>
                    <Suspense fallback={<Loading />}>
                        <CreditCardsContainer />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.CARD_DETAIL} isCompanyScoped canSee={cardsEnabled}>
                    <Suspense fallback={<Loading />}>
                        <CreditCardDetail />
                    </Suspense>
                </PrivateRoute>

                {/* Company-scoped transactions routes */}
                <PrivateRoute
                    exact
                    path={CompanyScopedRoutePathEnum.TRANSACTIONS}
                    isCompanyScoped
                    includedFreeVersion
                    canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.TRANSACTION__ALL__READ)}
                >
                    <Suspense fallback={<Loading />}>
                        <TransactionsList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.TRANSACTION_DETAIL} isCompanyScoped includedFreeVersion>
                    <Suspense fallback={<Loading />}>
                        <TransactionsDetails />
                    </Suspense>
                </PrivateRoute>

                {/* Company-scoped workflows routes */}
                <PrivateRoute exact path={CompanyScopedRoutePathEnum.WORKFLOWS} isCompanyScoped canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.WORKFLOW__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <WorkflowsList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute
                    exact
                    path={CompanyScopedRoutePathEnum.WORKFLOW_DETAIL}
                    isCompanyScoped
                    canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.WORKFLOW__ALL__READ)}
                >
                    <Suspense fallback={<Loading />}>
                        <WorkflowsList />
                    </Suspense>
                </PrivateRoute>

                {/* Company-scoped employees routes */}
                <PrivateRoute exact path={CompanyScopedRoutePathEnum.EMPLOYEES} isCompanyScoped includedFreeVersion>
                    <Suspense fallback={<Loading />}>
                        <EmployeesPage />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.EMPLOYEE_DETAIL} isCompanyScoped includedFreeVersion>
                    <Suspense fallback={<Loading />}>
                        <EmployeeDetail />
                    </Suspense>
                </PrivateRoute>

                {/* Company-scoped vendors routes */}
                <PrivateRoute exact path={CompanyScopedRoutePathEnum.VENDORS} isCompanyScoped canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <VendorsList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute
                    exact
                    path={CompanyScopedRoutePathEnum.VENDOR_DETAIL}
                    isCompanyScoped
                    canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__READ)}
                >
                    <Suspense fallback={<Loading />}>
                        <VendorDetail />
                    </Suspense>
                </PrivateRoute>

                {/* Company-scoped cost centers routes */}
                <PrivateRoute exact path={CompanyScopedRoutePathEnum.COST_CENTERS} isCompanyScoped>
                    <Suspense fallback={<Loading />}>
                        <CostCentersList />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.COST_CENTER_DETAIL} isCompanyScoped>
                    <Suspense fallback={<Loading />}>
                        <CostCenterDetail />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.LM} isCompanyScoped canSee={!!activeCompany?.liquidityManagementEnabled && AuthzService.canReadLiquidity()}>
                    <Suspense fallback={<Loading />}>
                        <LiquidityContainer />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.REPORTS} isCompanyScoped canSee={AuthzService.isRightGrantedForLoggedInUser(RightEnum.REPORTING__ALL__READ)}>
                    <Suspense fallback={<Loading />}>
                        <Reports />
                    </Suspense>
                </PrivateRoute>

                {/* Company-scoped purchase orders routes */}
                <PrivateRoute exact path={CompanyScopedRoutePathEnum.PURCHASE_ORDERS} isCompanyScoped isFeatureEnabled={isPurchaseOrderEnabled}>
                    <Suspense fallback={<Loading />}>
                        <PurchaseOrdersListPage />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.PURCHASE_ORDER_DETAIL} isCompanyScoped>
                    <Suspense fallback={<Loading />}>
                        <PurchaseOrderDetailsPage />
                    </Suspense>
                </PrivateRoute>

                {/* Company-scoped delivery notes routes */}
                <PrivateRoute exact path={CompanyScopedRoutePathEnum.DELIVERY_NOTES} isCompanyScoped isFeatureEnabled={isPurchaseOrderEnabled}>
                    <Suspense fallback={<Loading />}>
                        <DeliveryNotesListPage />
                    </Suspense>
                </PrivateRoute>

                <PrivateRoute exact path={CompanyScopedRoutePathEnum.DELIVERY_NOTE_DETAIL} isCompanyScoped>
                    <Suspense fallback={<Loading />}>
                        <DeliveryNoteDetailsPage />
                    </Suspense>
                </PrivateRoute>

                {/* Legacy redirect for old settings URLs */}
                {legacyRoutes.map((legacyRoute) => (
                    <Route exact={legacyRoute.exact} path={legacyRoute.path} key={legacyRoute.path}>
                        <LegacyRouteRedirect originalPath={legacyRoute.path} />
                    </Route>
                ))}

                <ErrorRoute path={RoutePathEnum.ERROR}>
                    <ErrorPage />
                </ErrorRoute>
                <FullScreenRoute path={RoutePathEnum.AUTH_ERROR}>
                    <AuthErrorPage />
                </FullScreenRoute>

                {/* this will redirect any URL without a route to "/not-found" */}
                <ErrorRoute path={RoutePathEnum.GLOBAL}>
                    <NotFoundPage />
                </ErrorRoute>
            </Switch>
        </Suspense>
    )
}
